import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip,
  Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, CircularProgress, alpha, Pagination, LinearProgress, InputBase,
} from '@mui/material';
import {
  Add as AddIcon, ContentCopy as CopyIcon, Edit as EditIcon,
  Delete as DeleteIcon, QrCode2 as QrIcon, BarChart as AnalyticsIcon,
  Download as DownloadIcon, TrendingUp, Lock as LockIcon,
  OpenInNew as OpenIcon, WarningAmber as WarningIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logoImg from '../assets/webcard-icon.png';
import { fmtDate } from '../utils/dateFormat';

const PRIMARY = '#00A7E3';
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : n);

const PLAN_QR_LIMITS = { free: 2, pro: 5, business: null };
const PAGE_SIZE = 7;

const generateBrandedQr = (text, size = 400) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    QRCode.toCanvas(canvas, text, {
      width: size, margin: 2, errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#ffffff' },
    }, (err) => {
      if (err) { reject(err); return; }
      const ctx = canvas.getContext('2d');
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const logoSize = size * 0.22;
        const x = (size - logoSize) / 2;
        const y = (size - logoSize) / 2;
        const pad = 6;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        const r = 8;
        ctx.moveTo(x - pad + r, y - pad);
        ctx.arcTo(x + logoSize + pad, y - pad, x + logoSize + pad, y + logoSize + pad, r);
        ctx.arcTo(x + logoSize + pad, y + logoSize + pad, x - pad, y + logoSize + pad, r);
        ctx.arcTo(x - pad, y + logoSize + pad, x - pad, y - pad, r);
        ctx.arcTo(x - pad, y - pad, x + logoSize + pad, y - pad, r);
        ctx.closePath();
        ctx.fill();
        ctx.drawImage(logo, x, y, logoSize, logoSize);
        resolve(canvas.toDataURL('image/png'));
      };
      logo.onerror = () => resolve(canvas.toDataURL('image/png'));
      logo.src = logoImg;
    });
  });
};

const QrManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const firstLoadRef = useRef(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [subStatus, setSubStatus] = useState(null);
  const [labelSearch, setLabelSearch] = useState('');
  const [debouncedLabelSearch, setDebouncedLabelSearch] = useState('');
  const [qrPreview, setQrPreview] = useState({ open: false, code: null, src: null });

  const prevDebouncedQ = useRef('');
  const lastFetchKey = useRef('');
  const [listRefresh, setListRefresh] = useState(0);

  const toast = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedLabelSearch(labelSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [labelSearch]);

  const fetchSubStatus = useCallback(async () => {
    try { const { data } = await api.get('/qr/subscription-status'); setSubStatus(data); } catch {}
  }, []);

  useEffect(() => { fetchSubStatus(); }, [fetchSubStatus]);

  const paramsForFetch = (p, q) => ({
    page: p,
    limit: PAGE_SIZE,
    ...(q ? { q } : {}),
  });

  useEffect(() => {
    const q = debouncedLabelSearch;
    const searchChanged = prevDebouncedQ.current !== q;
    prevDebouncedQ.current = q;
    const p = searchChanged ? 1 : page;
    if (searchChanged && page !== 1) setPage(1);

    const key = `${p}:${q}:${listRefresh}`;
    if (key === lastFetchKey.current) return;
    lastFetchKey.current = key;

    (async () => {
      try {
        if (firstLoadRef.current) setLoading(true);
        else setListLoading(true);
        const { data } = await api.get('/qr/my-codes', { params: paramsForFetch(p, q) });
        let rows = data.codes || [];
        let pag = data.pagination;
        let resolvedPage = p;
        if (p > 1 && rows.length === 0 && pag && (pag.total || 0) > 0) {
          resolvedPage = Math.max(1, p - 1);
          setPage(resolvedPage);
          const { data: d2 } = await api.get('/qr/my-codes', { params: paramsForFetch(resolvedPage, q) });
          rows = d2.codes || [];
          pag = d2.pagination;
        }
        setCodes(rows);
        setPagination(pag || null);
        setPage(resolvedPage);
      } catch (e) { toast(e.response?.data?.message || 'Failed to load', 'error'); }
      finally {
        setLoading(false);
        setListLoading(false);
        firstLoadRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page/debouncedLabelSearch/listRefresh drive fetch; search reset handled above
  }, [page, debouncedLabelSearch, listRefresh]);

  const hasAnalyticsAccess = subStatus?.hasAccess || user?.role === 'admin' || ['pro', 'business', 'premium'].includes(user?.plan);
  const plan = user?.plan === 'premium' ? 'pro' : (user?.plan || 'free');
  const qrLimit = PLAN_QR_LIMITS[plan];
  const isAdmin = user?.role === 'admin';
  const accountQrTotal = pagination?.accountTotal ?? pagination?.total ?? codes.length;
  const totalQr = pagination?.total ?? codes.length;
  const atLimit = qrLimit !== null && !isAdmin && accountQrTotal >= qrLimit;

  const totalScans = pagination?.totalScans ?? codes.reduce((s, c) => s + (c.scan_count || 0), 0);

  const downloadQr = async (code) => {
    const url = `https://app.webcard.digital/qr/${code.uid}`;
    const dataUrl = await generateBrandedQr(url, 600);
    const a = document.createElement('a');
    a.download = `qr-${code.label || code.uid}.png`;
    a.href = dataUrl;
    a.click();
  };

  const openPreview = async (code) => {
    setQrPreview({ open: true, code, src: null });
    try {
      const src = await generateBrandedQr(`https://app.webcard.digital/qr/${code.uid}`, 600);
      setQrPreview(p => ({ ...p, src }));
    } catch { setQrPreview(p => ({ ...p, src: 'error' })); }
  };

  const handleCreate = async () => {
    if (!newUrl.trim()) { toast('Please enter a destination URL', 'warning'); return; }
    try {
      await api.post('/qr/create', { label: newLabel.trim() || null, targetUrl: newUrl.trim() });
      setCreateOpen(false); setNewLabel(''); setNewUrl('');
      toast('QR code created!');
      setPage(1);
      lastFetchKey.current = '';
      setListRefresh((r) => r + 1);
    } catch (e) { toast(e.response?.data?.message || 'Failed to create', 'error'); }
  };

  const handleEdit = async () => {
    if (!selectedCode) return;
    try {
      await api.put(`/qr/${selectedCode.uid}`, { label: editLabel, targetUrl: editUrl });
      setEditOpen(false); toast('QR code updated');
      lastFetchKey.current = '';
      setListRefresh((r) => r + 1);
    } catch (e) { toast(e.response?.data?.message || 'Failed to update', 'error'); }
  };

  const handleDelete = async () => {
    if (!selectedCode) return;
    try {
      await api.delete(`/qr/${selectedCode.uid}`);
      setDeleteOpen(false); setSelectedCode(null);
      toast('QR code deleted');
      lastFetchKey.current = '';
      setListRefresh((r) => r + 1);
    } catch (e) { toast(e.response?.data?.message || 'Failed to delete', 'error'); }
  };

  const handleToggle = async (code) => {
    try {
      await api.put(`/qr/${code.uid}`, { isActive: !code.is_active });
      lastFetchKey.current = '';
      setListRefresh((r) => r + 1);
      toast(code.is_active ? 'QR deactivated' : 'QR activated');
    } catch { toast('Failed to toggle', 'error'); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text).then(() => toast('Copied!')); };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary">Loading QR codes...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>QR Codes</Typography>
        {atLimit ? (
          <Button variant="contained" size="small" onClick={() => navigate('/subscription')}
            sx={{ bgcolor: '#f59e0b', textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#d97706', boxShadow: 'none' } }}>
            Upgrade for More QR Codes
          </Button>
        ) : (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}>
            Generate QR Code
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {accountQrTotal === 0 ? 'Create dynamic QR codes and track every scan.' : (
          <>
            {debouncedLabelSearch
              ? `${totalQr} match${totalQr !== 1 ? 'es' : ''} · ${fmt(totalScans)} scan${totalScans !== 1 ? 's' : ''} in results`
              : `${totalQr} QR code${totalQr !== 1 ? 's' : ''} · ${fmt(totalScans)} total scan${totalScans !== 1 ? 's' : ''}`}
            {qrLimit !== null && !isAdmin ? ` · ${qrLimit} allowed on ${plan} plan` : ''}
          </>
        )}
      </Typography>

      {accountQrTotal > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, px: 2, py: 1,
          bgcolor: t => alpha(t.palette.text.primary, 0.03), borderRadius: 2,
          border: '1px solid', borderColor: 'divider',
          '&:focus-within': { borderColor: 'primary.main', bgcolor: 'background.paper' },
        }}>
          <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          <InputBase
            placeholder="Search by label…"
            value={labelSearch}
            onChange={(e) => {
              const v = e.target.value;
              setLabelSearch(v);
              if (!v.trim()) setDebouncedLabelSearch('');
            }}
            sx={{ flex: 1, fontSize: '0.9rem' }}
          />
        </Box>
      )}

      {/* Upgrade banner */}
      {!hasAnalyticsAccess && accountQrTotal > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
          mb: 3, p: 2, borderRadius: 2.5, border: '1px solid', borderColor: `${PRIMARY}30`, bgcolor: `${PRIMARY}06`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LockIcon sx={{ color: PRIMARY, fontSize: 20 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Unlock Scan Analytics</Typography>
              <Typography variant="caption" color="text.secondary">
                Upgrade to Pro or Business to see detailed scan data for your QR codes.
              </Typography>
            </Box>
          </Box>
          <Button variant="outlined" size="small" onClick={() => navigate('/subscription')}
            sx={{ borderColor: PRIMARY, color: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: `${PRIMARY}08` } }}>
            View Plans
          </Button>
        </Box>
      )}

      {/* Empty state — no codes at all */}
      {!loading && !listLoading && accountQrTotal === 0 && !debouncedLabelSearch ? (
        <Box sx={{ textAlign: 'center', py: 10, borderRadius: 3, border: '2px dashed', borderColor: 'divider' }}>
          <QrIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>No QR codes yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 380, mx: 'auto' }}>
            Create a dynamic QR code that links to any URL. Change the destination anytime — even after printing.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 4, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc' } }}>
            Generate Your First QR
          </Button>
        </Box>
      ) : !loading && !listLoading && totalQr === 0 && debouncedLabelSearch && accountQrTotal > 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>No QR codes match this label</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Try a different search or clear the filter.
          </Typography>
          <Button variant="outlined" size="small" onClick={() => { setLabelSearch(''); setDebouncedLabelSearch(''); }}
            sx={{ textTransform: 'none', borderRadius: 2 }}>Clear search</Button>
        </Box>
      ) : totalQr > 0 ? (
        <>
          {listLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: listLoading ? 0.55 : 1, transition: 'opacity 0.2s' }}>
            {codes.map(code => (
              <QrRow key={code.uid} code={code}
                onCopy={copy} onDownload={() => downloadQr(code)}
                onPreview={() => openPreview(code)}
                onEdit={() => { setSelectedCode(code); setEditLabel(code.label || ''); setEditUrl(code.target_url); setEditOpen(true); }}
                onDelete={() => { setSelectedCode(code); setDeleteOpen(true); }}
                onToggle={() => handleToggle(code)}
                onAnalytics={() => navigate('/analytics')}
                hasAnalytics={hasAnalyticsAccess} />
            ))}
          </Box>
          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                color="primary"
                count={pagination.totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
              />
            </Box>
          )}
        </>
      ) : null}

      {/* QR Preview Dialog */}
      <Dialog open={qrPreview.open} onClose={() => setQrPreview({ open: false, code: null, src: null })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380, width: '100%' } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 0, fontWeight: 700 }}>QR Code</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
          {qrPreview.src && qrPreview.src !== 'error' ? (
            <Box component="img" src={qrPreview.src} alt="QR"
              sx={{ width: 240, height: 240, borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
          ) : qrPreview.src === 'error' ? (
            <Typography color="error">Failed to generate QR</Typography>
          ) : (
            <CircularProgress size={32} sx={{ my: 4 }} />
          )}
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: 'text.primary' }}>
            {qrPreview.code?.label || 'Untitled QR'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Points to: {qrPreview.code?.target_url}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => copy(`https://app.webcard.digital/qr/${qrPreview.code?.uid}`)}
            startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}>Copy Link</Button>
          <Button variant="contained" size="small"
            onClick={() => { if (qrPreview.code) downloadQr(qrPreview.code); }}
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            disabled={!qrPreview.src || qrPreview.src === 'error'}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}>
            Download QR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Generate QR Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a dynamic QR code. You can change the destination anytime — even after printing.
          </Typography>
          <TextField fullWidth label="Label (optional)" value={newLabel} onChange={e => setNewLabel(e.target.value)}
            placeholder="e.g. Business Card, Event Poster" sx={{ mb: 2, mt: 1 }} size="small" />
          <TextField fullWidth label="Destination URL" value={newUrl} onChange={e => setNewUrl(e.target.value)}
            placeholder="e.g. https://google.com" required size="small"
            helperText="Where should people go when they scan this QR?" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0094cc' }, textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none' }}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit QR Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update the label or destination. The QR image stays the same — the redirect is dynamic.
          </Typography>
          <TextField fullWidth label="Label" value={editLabel} onChange={e => setEditLabel(e.target.value)} sx={{ mb: 2, mt: 1 }} size="small" />
          <TextField fullWidth label="Destination URL" value={editUrl} onChange={e => setEditUrl(e.target.value)} required size="small" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0094cc' }, textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: t => alpha(t.palette.error.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningIcon color="error" fontSize="small" />
          </Box>
          Delete QR Code
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete <strong>{selectedCode?.label || selectedCode?.uid}</strong>? All scan analytics will also be removed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════
//  QR Row component
// ═══════════════════════════════════════
function QrRow({ code, onCopy, onDownload, onPreview, onEdit, onDelete, onToggle, onAnalytics, hasAnalytics }) {
  const qrUrl = `https://app.webcard.digital/qr/${code.uid}`;
  const truncate = (s, n = 45) => s && s.length > n ? s.slice(0, n) + '...' : s;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'stretch', minHeight: 80,
      border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
      overflow: 'hidden', transition: 'box-shadow 0.2s, border-color 0.2s',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: `${PRIMARY}40` },
    }}>
      {/* Left accent */}
      <Box sx={{ width: 4, bgcolor: code.is_active ? PRIMARY : '#d4d4d8', flexShrink: 0 }} />

      {/* QR thumbnail */}
      <Box onClick={onPreview}
        sx={{ display: 'flex', alignItems: 'center', px: 2, cursor: 'pointer', '&:hover': { bgcolor: `${PRIMARY}06` } }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 1.5,
          bgcolor: `${PRIMARY}10`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <QrIcon sx={{ color: PRIMARY, fontSize: 28 }} />
        </Box>
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, py: 1.5, pr: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: 'text.primary' }}>
            {code.label || 'Untitled QR'}
          </Typography>
          <Chip label={`${fmt(code.scan_count || 0)} scan${(code.scan_count || 0) !== 1 ? 's' : ''}`}
            size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: (code.scan_count || 0) > 0 ? '#dcfce7' : '#f4f4f5', color: (code.scan_count || 0) > 0 ? '#166534' : '#71717a' }} />
          {!code.is_active && (
            <Chip label="Inactive" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#fef2f2', color: '#991b1b' }} />
          )}
        </Box>

        <Box onClick={() => window.open(code.target_url, '_blank')}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 0.25, '&:hover .dest-text': { color: PRIMARY } }}>
          <OpenIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
          <Typography className="dest-text" variant="caption" noWrap
            sx={{ color: 'text.secondary', fontSize: '0.75rem', transition: 'color 0.15s', maxWidth: { xs: 200, sm: 400 } }}>
            {truncate(code.target_url)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
          <Box onClick={() => onCopy(qrUrl)}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover .qr-url': { color: PRIMARY } }}>
            <Typography className="qr-url" variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'text.disabled', transition: 'color 0.15s' }}>
              /qr/{code.uid.slice(0, 8)}...
            </Typography>
            <CopyIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
            {fmtDate(code.created_at)}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: { xs: 0.5, sm: 1.5 }, flexShrink: 0 }}>
        <Tooltip title={code.is_active ? 'Deactivate' : 'Activate'} arrow>
          <Switch checked={!!code.is_active} onChange={onToggle} size="small" sx={{ mr: 0.5 }} />
        </Tooltip>
        <Tooltip title="Preview & download QR" arrow>
          <IconButton size="small" onClick={onPreview}
            sx={{ color: PRIMARY, '&:hover': { bgcolor: `${PRIMARY}10` } }}>
            <QrIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download QR" arrow>
          <IconButton size="small" onClick={onDownload}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: t => alpha(t.palette.text.primary, 0.06) } }}>
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {hasAnalytics && (
          <Tooltip title="Analytics" arrow>
            <IconButton size="small" onClick={onAnalytics}
              sx={{ color: 'text.secondary', '&:hover': { bgcolor: t => alpha(t.palette.text.primary, 0.06) } }}>
              <AnalyticsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Edit" arrow>
          <IconButton size="small" onClick={onEdit}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: t => alpha(t.palette.text.primary, 0.06) } }}>
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton size="small" onClick={onDelete}
            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: t => alpha(t.palette.error.main, 0.08) } }}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default QrManager;
